import { IconButton, Typography } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import React, { useState, useEffect } from 'react';
import moment from 'moment/moment';
import Colors from '../theme/colors';

function PlaidJsonTable(props) {
  // javascript version of the typescript in ExternalSyncDataTable.tsx, auto converted
  const [expandedRows, setExpandedRows] = useState({});
  const [isTableVisible, setIsTableVisible] = useState(false);

  // Extract omitFields from props with default empty array
  const { omitFields = [], title, data, autoExpand = true } = props;
  // Function to format key names: replace underscores with spaces and capitalize each word
  const formatKeyName = key => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Function to pre-populate expandedRows for automatic expansion
  const initializeExpandedRows = (data, parentKey = '') => {
    const initialState = {};

    if (!data || typeof data !== 'object') {
      return initialState;
    }

    // If it's an array, process each item
    if (Array.isArray(data)) {
      data.forEach((item, index) => {
        const rowKey = parentKey ? `${parentKey}-${index}` : `${index}`;
        Object.assign(initialState, { [rowKey]: true });

        if (typeof item === 'object' && item !== null) {
          Object.keys(item).forEach(key => {
            const childValue = item[key];
            const childRowKey = `${rowKey}-${key}`;

            if (typeof childValue === 'object' && childValue !== null) {
              Object.assign(initialState, { [childRowKey]: true });
              Object.assign(
                initialState,
                initializeExpandedRows(childValue, childRowKey)
              );
            }
          });
        }
      });
    } else {
      // If it's an object, process each property
      Object.keys(data).forEach(key => {
        const childValue = data[key];
        const rowKey = parentKey ? `${parentKey}-${key}` : `root-${key}`;

        if (typeof childValue === 'object' && childValue !== null) {
          Object.assign(initialState, { [rowKey]: true });
          Object.assign(
            initialState,
            initializeExpandedRows(childValue, rowKey)
          );
        }
      });
    }

    return initialState;
  };

  useEffect(() => {
    if (autoExpand && data) {
      const initialState = initializeExpandedRows(data);
      setExpandedRows(initialState);
    }
  }, [data, autoExpand]);

  const toggleRowAndExpandChildren = (rowKey, row) => {
    setExpandedRows(prev => {
      const newState = { ...prev };
      newState[rowKey] = !prev[rowKey];
      // If we're expanding the current row, also expand all immediate children
      if (newState[rowKey] && typeof row === 'object' && row !== null) {
        Object.keys(row).forEach(key => {
          const childValue = row[key];
          if (typeof childValue === 'object' && childValue !== null) {
            const childRowKey = `${rowKey}-${key}`;
            newState[childRowKey] = true;
          }
        });
      }
      return newState;
    });
  };

  // Helper function to get legal name from account
  const getLegalName = (account) => {
    return account.legal_name || '';
  };

  // Helper function to get Plaid phone from PlaidItem
  const getPlaidPhone = (account) => {
    if (account.phone_numbers && Array.isArray(account.phone_numbers)) {
      const primaryPhone = account.phone_numbers.find(phone => phone.primary === true);
      return primaryPhone ? primaryPhone.phone_number : '';
    }
    return '';
  };

  // Helper function to get Plaid identity address from PlaidItem
  const getPlaidIdentityAddress = (account) => {
    if (account.mailing_address && Array.isArray(account.mailing_address)) {
      const primaryAddress = account.mailing_address.find(addr => addr.primary === true);
      if (primaryAddress) {
        const parts = [];
        if (primaryAddress.street) parts.push(primaryAddress.street);
        if (primaryAddress.city) parts.push(primaryAddress.city);
        if (primaryAddress.state) parts.push(primaryAddress.state);
        if (primaryAddress.zip_code) parts.push(primaryAddress.zip_code);
        return parts.join(', ');
      }
    }
    return '';
  };

  const renderRow = (row, rowKey = 'root', level = 0) => {
    return (
      Object.entries(row)
        // Filter out fields that should be omitted
        .filter(([key]) => !omitFields.includes(key))
        .map(([key, value]) => {
          const isNested = typeof value === 'object' && value !== null;
          const currentRowKey = `${rowKey}-${key}`;

          return (
            <React.Fragment key={currentRowKey}>
              <tr
                className={`border-b ${isNested ? 'cursor-pointer' : ''}`}
                onClick={
                  isNested
                    ? () => toggleRowAndExpandChildren(currentRowKey, value)
                    : null
                }
                style={{ fontFamily: 'monospace' }}
              >
                <td
                  className="border px-4 py-2"
                  style={{
                    paddingLeft: `${level * 20}px`,
                    paddingRight: '30px',
                    minWidth: '200px'
                  }}
                >
                  {isNested ? (
                    <span className="flex items-center cursor-pointer gap-1">
                      <span className="text-xl" style={{ cursor: 'pointer' }}>
                        {expandedRows[currentRowKey] ? '▼' : '▶'}
                      </span>
                      <strong>{formatKeyName(key)}</strong>
                    </span>
                  ) : (
                    <strong>{formatKeyName(key)}</strong>
                  )}
                </td>
                <td
                  className="border px-4 py-2"
                  style={{
                    paddingLeft: '30px',
                    paddingRight: '30px',
                    minWidth: '250px'
                  }}
                >
                  {!isNested
                    ? value !== null && value !== undefined
                      ? key === 'created' || key === 'modified'
                        ? moment(value).format('l LTS')
                        : String(value)
                      : ''
                    : ''}
                </td>
              </tr>

              {isNested && expandedRows[currentRowKey] && (
                <tr>
                  <td colSpan={2} className="p-0">
                    <table
                      className="w-full border-l border-gray-300"
                      style={{
                        borderSpacing: '10px',
                        borderCollapse: 'separate',
                        width: '100%'
                      }}
                    >
                      <tbody>
                        {renderRow(value, currentRowKey, level + 1)}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })
    );
  };

  return (
    <div className="overflow-x-auto" style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {title && (
          <Typography style={{ color: 'black', display: 'inline' }}>
            {title}
          </Typography>
        )}
        <IconButton
          onClick={() => setIsTableVisible(!isTableVisible)}
          className="inline-flex items-center"
          style={{ cursor: 'pointer' }}
        >
          {isTableVisible ? (
            <Visibility style={{ color: 'green', fontSize: '24px' }} />
          ) : (
            <VisibilityOff style={{ color: 'red', fontSize: '24px' }} />
          )}
        </IconButton>
      </div>

      {isTableVisible && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
          {Array.isArray(data) ? (
            data.map((item, index) => {
              const legalName = getLegalName(item);
              const plaidPhone = getPlaidPhone(item);
              const plaidIdentityAddress = getPlaidIdentityAddress(item);

              return (
                <div
                  key={index}
                  style={{
                    flex: '1 1 300px',
                    maxWidth: 'calc(48% - 15px)',
                    margin: '5px',
                    border: `solid 2px ${
                      item.default ? 'green' : item.newer ? Colors.info : 'gray'
                    }`,
                    width: '100%',
                    padding: '5px',
                    borderCollapse: 'collapse',
                    position: 'relative',
                    borderRadius: '8px'
                  }}
                >
                  {item.default && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '0',
                        right: '0',
                        color: 'white',
                        padding: '2px 6px',
                        borderBottomLeftRadius: '4px',
                        backgroundColor: 'green'
                      }}
                    >
                      <Typography style={{ color: 'white' }}>Default</Typography>
                    </div>
                  )}
                  {item.newer && (
                    <div
                      style={{
                        position: 'absolute',
                        top: item.default ? '28px' : '0',
                        right: '0',
                        color: 'white',
                        padding: '2px 6px',
                        borderBottomLeftRadius: '4px',
                        backgroundColor: Colors.info
                      }}
                    >
                      <Typography style={{ color: 'white' }}>Newer</Typography>
                    </div>
                  )}

                  {/* Display Legal Name, Plaid Phone, and Plaid Identity Address at the top */}
                  {(legalName || plaidPhone || plaidIdentityAddress) && (
                    <div
                      style={{
                        padding: '10px',
                        marginBottom: '10px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        borderBottom: '1px solid #ddd'
                      }}
                    >
                      {legalName && (
                        <Typography
                          variant="body2"
                          style={{ marginBottom: '4px', fontSize: '14px' }}
                        >
                          <strong>Legal Name: </strong>{legalName}
                        </Typography>
                      )}
                      {plaidPhone && (
                        <Typography
                          variant="body2"
                          style={{ marginBottom: '4px', fontSize: '14px' }}
                        >
                          <strong>Plaid Phone:</strong> {plaidPhone}
                        </Typography>
                      )}
                      {plaidIdentityAddress && (
                        <Typography
                          variant="body2"
                          style={{ fontSize: '14px' }}
                        >
                          <strong>Plaid Identity Address:</strong> {plaidIdentityAddress}
                        </Typography>
                      )}
                    </div>
                  )}

                  <table
                    style={{
                      borderSpacing: '10px',
                      borderCollapse: 'separate',
                      width: '100%'
                    }}
                  >
                    <tbody>{renderRow(item, `${index}`)}</tbody>
                  </table>
                </div>
              );
            })
          ) : (
            <div
              style={{
                flex: '1 1 100%',
                border: `solid 2px ${data && data.default ? 'green' : 'gray'}`,
                width: '100%',
                padding: '5px',
                borderCollapse: 'collapse',
                borderRadius: '8px'
              }}
            >
              {data?.default && (
                <div
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    color: 'white',
                    padding: '2px 6px',
                    borderBottomLeftRadius: '4px',
                    backgroundColor: 'green'
                  }}
                >
                  <Typography style={{ color: 'white' }}>Default</Typography>
                </div>
              )}
              {data?.newer && (
                <div
                  style={{
                    position: 'absolute',
                    top: data?.default ? '28px' : '0',
                    right: '0',
                    color: 'white',
                    padding: '2px 6px',
                    borderBottomLeftRadius: '4px',
                    backgroundColor: 'blue'
                  }}
                >
                  <Typography style={{ color: 'white' }}>Newer</Typography>
                </div>
              )}
              <table
                style={{
                  borderSpacing: '10px',
                  borderCollapse: 'separate',
                  width: '100%'
                }}
              >
                <tbody>{renderRow(data, 'root')}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PlaidJsonTable;
